import { useParams, Link } from 'react-router-dom'
import { useSport } from '@/hooks/useSport'
import { useApi } from '@/hooks/useApi'
import { fetchPost } from '@/lib/api'
import DOMPurify from 'dompurify'
import { Skeleton } from '@/components/ui/Skeleton'

export default function BracketologyShow() {
  const { sport } = useSport()
  const { slug } = useParams<{ slug: string }>()

  const { data: post, loading } = useApi(
    () => slug ? fetchPost(sport, slug).then(d => d.post) : Promise.reject('No slug'),
    [sport, slug],
  )

  if (loading) return (
    <div className="max-w-3xl mx-auto space-y-4">
      <Skeleton variant="text" lines={1} className="w-1/3" />
      <Skeleton variant="text" lines={1} className="w-2/3" />
      <Skeleton variant="text" lines={8} />
    </div>
  )

  if (!post) return null

  const cleanHtml = DOMPurify.sanitize(post.content_html || '', {
    ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong', 'em', 'a', 'ul', 'ol', 'li',
                   'blockquote', 'br', 'span', 'div', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'img', 'b', 'i', 'u'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'class', 'width', 'height'],
    ALLOW_DATA_ATTR: false,
  })

  return (
    <article className="max-w-3xl mx-auto">
      <Link
        to={`/${sport}/bracketology`}
        className="inline-flex items-center gap-1 text-sm text-accent hover:text-accent-light transition-colors mb-6"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to posts
      </Link>

      <header className="mb-8">
        <span className="text-sm font-semibold text-accent uppercase tracking-wider">
          {post.category?.replace('_', ' ')}
        </span>
        <h1 className="text-3xl sm:text-4xl font-bold text-white mt-2 leading-tight">
          {post.title}
        </h1>
        <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-slate-500">
          {post.published_at && (
            <time>{new Date(post.published_at).toLocaleDateString('en-US', {
              month: 'long', day: 'numeric', year: 'numeric',
            })}</time>
          )}
          {post.source && (
            <>
              <span>&middot;</span>
              <span>{post.source}</span>
            </>
          )}
          {post.source_url && (
            <a
              href={post.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:underline"
            >
              Original source &rarr;
            </a>
          )}
        </div>
      </header>

      <div
        className="prose-d1"
        dangerouslySetInnerHTML={{ __html: cleanHtml }}
      />
    </article>
  )
}
